// ─────────────────────────────────────────────────────────────────────────────
// EQ DOMAINS & SUBSCALES — Goleman-framework inspired
// 5 domains · 4 subscales each · rich descriptors for reporting
// ─────────────────────────────────────────────────────────────────────────────

export const DOMAINS = [
  {
    id:"SA", name:"Self-Awareness",
    shortName:"Self-Awareness",
    icon:"🔍", color:"#7c3aed", bg:"#f5f3ff",
    description:"The ability to recognise your emotions, understand how they affect your thoughts and behaviour, know your strengths and weaknesses, and have self-confidence.",
    lowLabel:"Limited self-insight", highLabel:"Deep self-awareness",
    subscales:["Emotional Awareness","Accurate Self-Assessment","Self-Confidence","Emotional Literacy"],
    workplaceImpact:{
      low:"May be blindsided by emotional reactions, lack insight into how their behaviour affects others, and struggle with feedback.",
      high:"Demonstrates strong self-insight, uses emotions as information, openly acknowledges limitations and builds trust through authenticity.",
    },
    concerns:{
      low:["Limited insight into emotional triggers reduces leadership effectiveness","Difficulty receiving feedback constructively","May be perceived as defensive or lacking self-awareness"],
      high:["Excessive self-focus can slow decision-making","Over-analysis of emotions may create self-consciousness in high-pressure situations"],
    },
    development:{
      low:["Begin a daily emotion journal — note triggers, feelings and responses","Seek structured 360° feedback and explore themes honestly","Work with an executive coach on developing inner observer capacity"],
      high:["Channel self-awareness outward — use insights to strengthen empathy for others","Apply self-knowledge to coach and develop team members","Practise making decisions without needing complete emotional clarity first"],
    },
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited emotional self-knowledge; reactions often surprising to self and others"},
      {label:"Effective",   range:[4,7],  desc:"Generally aware of emotions and their impact; growing capacity for self-reflection"},
      {label:"Exceptional", range:[8,10], desc:"Deep, nuanced self-awareness used actively to regulate behaviour and inspire others"},
    ],
  },
  {
    id:"SR", name:"Self-Regulation",
    shortName:"Self-Regulation",
    icon:"⚖", color:"#0891b2", bg:"#cffafe",
    description:"The ability to control or redirect disruptive emotions and impulses, adapt to changing circumstances, and maintain integrity and trustworthiness.",
    lowLabel:"Reactive / Impulsive", highLabel:"Composed / Disciplined",
    subscales:["Emotional Control","Adaptability","Integrity","Impulse Control"],
    workplaceImpact:{
      low:"Emotional volatility disrupts team climate. Impulsive decisions create risk. Trust is eroded by inconsistency between stated and actual behaviour.",
      high:"Maintains composure under pressure. Creates psychological safety through predictability. Makes deliberate decisions that earn trust.",
    },
    concerns:{
      low:["Emotional reactivity damages relationships and team morale","Impulsive decisions create unnecessary risk","Perceived as inconsistent or untrustworthy"],
      high:["Over-control may suppress important emotional signals","Can appear cold or disengaged to emotionally expressive colleagues","May over-suppress conflict rather than addressing it"],
    },
    development:{
      low:["Practise a 90-second pause before responding in emotionally charged situations","Identify your top 3 emotional triggers and develop specific coping strategies","Build a trusted feedback partner to hold you accountable for behavioural consistency"],
      high:["Practise expressing emotions appropriately — vulnerability builds connection","Use your composure to create space for others to share concerns","Develop comfort with productive conflict — not all tension needs to be managed away"],
    },
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Reactive under pressure; impulse control and consistency are key development areas"},
      {label:"Effective",   range:[4,7],  desc:"Generally composed; maintains integrity in most situations with occasional lapses under stress"},
      {label:"Exceptional", range:[8,10], desc:"Exemplary composure and trustworthiness; a stabilising presence that others rely on"},
    ],
  },
  {
    id:"MO", name:"Motivation",
    shortName:"Motivation",
    icon:"🚀", color:"#c8964e", bg:"#fef3c7",
    description:"The drive to achieve beyond expectations, align with organisational goals, show initiative, and maintain optimism even in the face of setbacks.",
    lowLabel:"Low drive / Passive", highLabel:"Highly driven / Purposeful",
    subscales:["Achievement Drive","Commitment","Initiative","Optimism"],
    workplaceImpact:{
      low:"May meet minimum expectations but rarely go beyond them. Waits for direction rather than seeking opportunities. Pessimism can be contagious.",
      high:"Sets and pursues ambitious goals. Energises teams with contagious optimism. Takes initiative without prompting and inspires commitment in others.",
    },
    concerns:{
      low:["Lack of drive limits career progression and team contribution","Passivity frustrates high-performance cultures","Pessimism undermines collective resilience"],
      high:["Over-ambition can lead to burnout or unrealistic goal-setting","High drive may create pressure for colleagues","Optimism bias can lead to underestimating risks"],
    },
    development:{
      low:["Reconnect with your core purpose — what makes your work meaningful?","Set one stretching goal per quarter with a specific action plan","Build a habit of proactive problem-solving: identify one issue per week and propose a solution"],
      high:["Develop sustainable ambition — build recovery into your rhythm","Coach and mentor others to leverage your drive for collective impact","Balance optimism with rigorous risk assessment in high-stakes decisions"],
    },
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Disengaged or passive; relies on external motivation rather than internal drive"},
      {label:"Effective",   range:[4,7],  desc:"Committed to work; shows initiative and generally optimistic with room for more proactive drive"},
      {label:"Exceptional", range:[8,10], desc:"Highly self-motivated, purposeful leader who energises others and pursues excellence relentlessly"},
    ],
  },
  {
    id:"EM", name:"Empathy",
    shortName:"Empathy",
    icon:"🤝", color:"#16a34a", bg:"#dcfce7",
    description:"The ability to understand the emotional makeup of other people, treat people according to their emotional reactions, and create genuine connection.",
    lowLabel:"Limited empathy", highLabel:"Deeply empathetic",
    subscales:["Understanding Others","Developing Others","Service Orientation","Leveraging Diversity"],
    workplaceImpact:{
      low:"Struggles to read the room. May miss important emotional signals. Relationships can feel transactional. Diversity of perspectives is underutilised.",
      high:"Creates psychological safety and belonging. Builds deep, productive relationships. Leverages diversity as a strategic advantage.",
    },
    concerns:{
      low:["Missed emotional cues lead to avoidable conflict and disengagement","Inability to adapt communication style limits influence","Undervaluing diverse perspectives reduces decision quality"],
      high:["Over-empathy can lead to difficulty holding people accountable","May prioritise harmony over necessary hard conversations","Risk of absorbing others' stress (compassion fatigue)"],
    },
    development:{
      low:["Practise active listening — listen to understand, not to respond","Ask three questions before sharing your own perspective in conversations","Seek exposure to diverse teams and contexts outside your comfort zone"],
      high:["Pair empathy with clear expectations — care doesn't preclude accountability","Set boundaries to protect your own wellbeing while supporting others","Use your empathic insight to develop others as a coach or mentor"],
    },
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited emotional attunement; relationships tend to be transactional or unidirectional"},
      {label:"Effective",   range:[4,7],  desc:"Genuine interest in others; generally empathetic with capacity to develop deeper connection"},
      {label:"Exceptional", range:[8,10], desc:"Profoundly empathetic; creates inclusive, high-trust environments wherever they lead"},
    ],
  },
  {
    id:"SS", name:"Social Skills",
    shortName:"Social Skills",
    icon:"💬", color:"#dc2626", bg:"#fef2f2",
    description:"Proficiency in managing relationships and building networks; the ability to find common ground, influence others, and communicate with clarity and impact.",
    lowLabel:"Limited social effectiveness", highLabel:"Highly socially skilled",
    subscales:["Influence","Communication","Conflict Management"],
    workplaceImpact:{
      low:"Struggles to build coalitions, communicate with impact or manage conflict constructively. Influence is limited to formal authority.",
      high:"Inspires and influences across all levels. A skilled communicator who creates clarity and alignment. Transforms conflict into collaboration.",
    },
    concerns:{
      low:["Limited influence reduces ability to drive change through others","Poor conflict management allows issues to fester","Communication gaps create misalignment and wasted effort"],
      high:["High social skill can mask low empathy if not grounded in genuine care","Risk of being perceived as manipulative if influence is seen as self-serving","Over-emphasis on harmony can avoid necessary direct communication"],
    },
    development:{
      low:["Invest in public speaking and structured communication training","Practise building alliances before major meetings or decisions","Develop a conflict resolution framework and use it consistently"],
      high:["Ensure your influence is grounded in shared purpose, not just personal persuasion","Use your social capital to sponsor and amplify others","Develop depth alongside breadth — prioritise fewer, deeper relationships"],
    },
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Social effectiveness is limited; communication, influence and conflict skills need development"},
      {label:"Effective",   range:[4,7],  desc:"Generally effective in relationships; can influence and communicate well in most contexts"},
      {label:"Exceptional", range:[8,10], desc:"Masterful relationship builder; creates alignment, manages conflict and inspires through exceptional communication"},
    ],
  },
];

export const DOMAIN_MAP = Object.fromEntries(DOMAINS.map(d=>[d.id,d]));

export const NORMS = {
  SA:[5,8], SR:[5,8], MO:[5,8], EM:[5,8], SS:[5,8],
};

export const SUBSCALE_NORMS = {
  "Emotional Awareness":       [4,7],
  "Accurate Self-Assessment":  [5,8],
  "Self-Confidence":           [5,8],
  "Emotional Literacy":        [4,7],
  "Emotional Control":         [5,8],
  "Adaptability":              [5,8],
  "Integrity":                 [6,9],
  "Impulse Control":           [4,7],
  "Achievement Drive":         [5,8],
  "Commitment":                [5,8],
  "Initiative":                [5,8],
  "Optimism":                  [5,8],
  "Understanding Others":      [5,8],
  "Developing Others":         [4,7],
  "Service Orientation":       [5,8],
  "Leveraging Diversity":      [5,8],
  "Influence":                 [5,8],
  "Communication":             [5,8],
  "Conflict Management":       [4,7],
};
